// Promotions — what this rider has been given, and which of it is live.
//
// Was a collapsed accordion with four `variant="h2"` headings and a full promo card
// per row. Now hairline rows in a `SectionCard`: code, what it is and when it landed,
// the discount on the trailing edge. The four buckets and the rules that sort a promo
// into them are untouched — only how they are drawn changed.
import { useCallback, useEffect, useState } from 'react';
import {
  Stack,
  Box,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
  AutocompleteInline,
  Chip,
} from '../../../ui/core';
import { scrollY, hairline, softFill } from '../../../ui/styles';
import { CircleIcon, DeleteIcon, CheckBoxBlankIcon, CheckBoxIcon, DollarBadgeIcon } from '../../../ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useFormatters, useToast } from '../../../hooks';
import { getPromotions, removePromotion, getAvailablePromotions, getRideHistory } from '../../../redux/Users/actions';
import { SectionCard, SectionLabel, EmptyRows } from '../../../pages/DriverProfile/components';
import {
  Row,
  TrailingMeta,
  Meta,
  IconAction,
  SectionSkeleton,
  SectionError,
  useSectionFetch,
} from '../../../pages/UserProfile/parts';
import api from '../../../lib/api';
import moment from 'moment';

/** A bucket boundary inside the rows — tinted like a header, sized like a label. */
const GroupHeader = ({ children }) => (
  <Box
    sx={{
      px: 1.75,
      py: 0.75,
      borderTop: (t) => `1px solid ${hairline(t)}`,
      backgroundColor: (t) => softFill(t),
    }}
  >
    <SectionLabel>{children}</SectionLabel>
  </Box>
);

const Promotion = ({ promotion }) => {
  const f = useFormatters();
  const options = promotion.options || [];

  return (
    <Stack spacing="4px">
      <Box gap="12px" display="flex" alignItems="flex-start">
        <Typography fontWeight={400}>{promotion.code}</Typography>
        <Typography variant="body2" color="text.secondary">
          {promotion.subtitle || promotion.description}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {promotion.addedOn ? f.date.toLocaleString(promotion.addedOn, f.date.DateFormats.DATE_SHORT) : ''}
        </Typography>
      </Box>
      <Box gap="4px" display="flex" alignItems="center" flexWrap="wrap">
        {options.map((option) => {
          if (option.type === 'percent') {
            return <Chip key={option.id} label={`${option.amount}% Off`} size="small" />;
          }
          if (option.type === 'usd') {
            return <Chip key={option.id} label={`$${option.amount} Off`} size="small" />;
          }
          return null;
        })}
      </Box>
    </Stack>
  );
};

const AddPromotionDialog = ({ user, open, onClose }) => {
  const dispatch = useDispatch();
  const t = useToast();
  const availablePromotions = useSelector((state) => state.users.selection.availablePromotions);
  const userPromotions = useSelector((state) => state.users.selection.promotions);

  const options =
    availablePromotions && userPromotions
      ? availablePromotions.filter((p) => !userPromotions.find((up) => up.id === p.id))
      : [];
  const [value, setValue] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const hasChanges = value.length > 0;

  useEffect(() => {
    const timer = setTimeout(() => {
      getAvailablePromotions(inputValue)(dispatch);
    }, 500);
    return () => clearTimeout(timer);
  }, [inputValue, dispatch]);

  const isSelected = (o) => !!value.find((d) => d.id === o.id);

  const onInputChange = (_, value, reason) => {
    if (reason === 'input') {
      setInputValue(value);
    }
  };

  const save = async () => {
    setIsSaving(true);
    try {
      const results = [];
      for (const p of value) {
        results.push(await api.promotions.addPromotionToUser({ userId: user.id, promotionId: p.id }));
      }
      const addedCount = results.filter((r) => !r.alreadyExists).length;
      if (addedCount === 0) {
        t.info('Already on this account');
      } else {
        t.success(addedCount > 1 ? 'Promotions added' : 'Promotion added');
      }
      onClose();
    } catch (e) {
      t.error(e?.message || 'Could not add promotion');
    } finally {
      // Refresh in finally so a partial failure (some adds succeeded, a later
      // one threw) still reflects the promos that WERE added, instead of
      // leaving the store stale until a page refresh.
      await getPromotions(user.id)(dispatch);
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent sx={{ minWidth: 600, minHeight: 400, padding: 0 }}>
        <AutocompleteInline
          sx={{
            '.MuiInputBase-root': {
              padding: '12px 16px',
              width: '100%',
            },
          }}
          open
          freeSolo
          multiple
          loading={!availablePromotions}
          autoSelect={false}
          renderTags={() => null}
          placeholder="Add promotions to user"
          filterOptions={(x) => x}
          options={options}
          getOptionLabel={(option) => option.id}
          isOptionEqualToValue={(o, v) => v.id === o.id}
          renderOption={(props, o) => (
            <Box key={o.id} component="li" sx={{ display: 'flex', padding: '10px 20px !important' }} {...props}>
              <Box mr="8px" mt="4px" display="flex">
                {isSelected(o) ? (
                  <CheckBoxIcon sx={{ color: 'text.secondary' }} />
                ) : (
                  <CheckBoxBlankIcon sx={{ color: 'text.secondary' }} />
                )}
              </Box>
              <Promotion promotion={o} />
            </Box>
          )}
          inputValue={inputValue}
          onInputChange={onInputChange}
          onChange={(_, v) => setValue(v)}
        />
      </DialogContent>
      <Button
        sx={{
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
          width: '100%',
          padding: '20px',
        }}
        size="large"
        variant="contained"
        color="primary"
        disabled={!hasChanges}
        loading={isSaving}
        onClick={save}
      >
        Confirm
      </Button>
    </Dialog>
  );
};

/** "20% off", "$5 off · 10% off" — the discount, as words, on the trailing edge. */
const discountSummary = (promotion) =>
  (promotion.options || [])
    .map((o) => (o.type === 'percent' ? `${o.amount}% off` : o.type === 'usd' ? `$${o.amount} off` : null))
    .filter(Boolean)
    .join(' · ');

const PromotionRow = ({ promotion, waiting, onRemove, active, removable = active, first }) => {
  const f = useFormatters();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const closeConfirmDialog = () => {
    setShowConfirmDialog(false);
  };

  const onRemoveConfirmed = () => {
    setShowConfirmDialog(false);
    onRemove(promotion);
  };

  const added = promotion.addedOn ? f.date.toLocaleString(promotion.addedOn, f.date.DateFormats.DATE_SHORT) : null;

  return (
    <>
      <Row
        first={first}
        label={
          <Stack direction="row" alignItems="center" spacing={1}>
            {/* The dot is the only thing that says "this one is live right now". It
                stays on the active rows and off the other three buckets, which have a
                heading saying what they are instead. */}
            {active && <CircleIcon sx={{ fontSize: '9px', color: 'success.main', flexShrink: 0 }} />}
            <span>{promotion.code}</span>
          </Stack>
        }
        meta={[promotion.subtitle || promotion.description, added].filter(Boolean).join(' · ')}
        right={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Meta>{discountSummary(promotion)}</Meta>
            {removable && (
              <IconButton
                disabled={waiting}
                size="small"
                aria-label={`Remove promotion ${promotion.code}`}
                title="Remove promotion"
                onClick={() => setShowConfirmDialog(true)}
                sx={{ width: 26, height: 26 }}
              >
                {waiting ? (
                  <CircularProgress size={15} color="inherit" />
                ) : (
                  <DeleteIcon sx={{ color: 'error.main', fontSize: 16 }} />
                )}
              </IconButton>
            )}
          </Stack>
        }
      />
      <Dialog open={showConfirmDialog} onClose={closeConfirmDialog}>
        <DialogContent sx={{ maxWidth: '400px' }}>
          <Typography>Are you sure you want to remove promotion</Typography>
          <Typography sx={{ fontWeight: 700 }} component="span">
            {promotion.code}
          </Typography>
          ?
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={closeConfirmDialog}>
            Cancel
          </Button>
          <Button onClick={onRemoveConfirmed}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const PromotionsCard = ({ user }) => {
  const dispatch = useDispatch();
  const t = useToast();
  const [waitingFor, setWaitingFor] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const data = useSelector((state) => state.users.selection.promotions);
  const rideHistory = useSelector((state) => state.users.selection.rideHistory);

  const activePromos = [];
  const usedPromos = [];
  const expiredPromos = [];
  const unavailablePromos = [];
  if (data && rideHistory) {
    const now = moment();
    data.forEach((promo) => {
      if (promo.usesLeft <= 0) {
        return usedPromos.push(promo);
      }
      if (promo.endsOn && now.isAfter(moment(promo.endsOn))) {
        return expiredPromos.push(promo);
      }
      if (promo.endOn && now.isAfter(moment(promo.endOn))) {
        return expiredPromos.push(promo);
      }
      if (promo.startsOn && now.isBefore(moment(promo.startsOn))) {
        return unavailablePromos.push(promo);
      }
      if (promo.startOn && now.isBefore(moment(promo.startOn))) {
        return unavailablePromos.push(promo);
      }
      if (!!promo.beforeEventCount && rideHistory.completed && rideHistory.completed + 1 >= promo.beforeEventCount) {
        return expiredPromos.push(promo);
      }
      if (!!promo.afterEventCount && rideHistory.completed && rideHistory.completed < promo.afterEventCount) {
        return unavailablePromos.push(promo);
      }
      activePromos.push(promo);
    });
  }

  // Was `useEffect(…, [])`, so the promotions on screen belonged to whichever user was
  // opened first in the session.
  const load = useCallback(() => getPromotions(user.id)(dispatch), [user.id, dispatch]);
  const [status, retry] = useSectionFetch(load);

  // Every bucket above is computed from `data && rideHistory`, and ride history is
  // fetched by a card on the RIDER tab. On Account that selector is null, so all four
  // buckets came out empty and the section rendered as "no promotions" for a user who
  // has them — a hole the tab split opened. One page of history is all `completed` is
  // read from, and it is skipped when the Rider tab has already loaded it.
  useEffect(() => {
    if (!rideHistory) getRideHistory(user.id, 0, 5)(dispatch);
  }, [rideHistory, user.id, dispatch]);

  const onRemovePromotion = async (promotion) => {
    setWaitingFor(promotion.id);
    const { success } = await removePromotion(user.id, promotion.id)(dispatch);
    success ? t.success(`Promotion has been removed`) : t.error(`Something went wrong`);
    setWaitingFor(null);
  };

  const rows = (list, props) =>
    list.map((p, i) => (
      <PromotionRow
        key={p.id}
        first={i === 0}
        promotion={p}
        waiting={waitingFor === p.id}
        onRemove={onRemovePromotion}
        {...props}
      />
    ));

  return (
    <SectionCard
      title="Promotions"
      icon={<DollarBadgeIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
      meta={
        <TrailingMeta>
          <Stack direction="row" alignItems="center" spacing={1}>
            {data && <Meta>{activePromos.length} active</Meta>}
            <IconAction kind="add" label="Add a promotion" onClick={() => setShowAddDialog(true)} />
          </Stack>
        </TrailingMeta>
      }
    >
      <Box sx={{ maxHeight: 340, ...scrollY }}>
        {status === 'loading' && <SectionSkeleton />}
        {status === 'failed' && <SectionError onRetry={retry}>Couldn&rsquo;t load promotions.</SectionError>}
        {status === 'done' && activePromos.length === 0 && <EmptyRows>No active promotions.</EmptyRows>}
        {rows(activePromos, { active: true })}

        {/* The three inactive buckets keep their headings rather than merging into one
            "past" list: expired and used are answers to "why didn't my code work",
            and unavailable is an answer to "not yet" — which is a different reply. */}
        {expiredPromos.length > 0 && <GroupHeader>Expired</GroupHeader>}
        {rows(expiredPromos, { removable: true })}
        {usedPromos.length > 0 && <GroupHeader>Used</GroupHeader>}
        {rows(usedPromos)}
        {unavailablePromos.length > 0 && <GroupHeader>Not yet available</GroupHeader>}
        {rows(unavailablePromos)}
      </Box>
      {showAddDialog && <AddPromotionDialog user={user} open onClose={() => setShowAddDialog(false)} />}
    </SectionCard>
  );
};

export default PromotionsCard;
